import {
  ArchiveIcon,
  CubeIcon,
  FileIcon,
  ImageIcon,
  LayersIcon,
  MagicWandIcon,
} from '@radix-ui/react-icons';
import { useUIStore } from '../../stores';
import type { ToolpadId } from '../../types';
import type { MessageKey } from '../../i18n';
import { SidebarColumn, type SidebarRailItem } from './SidebarColumn';
import { SceneList } from './ToolPads/SceneList';
import { Library } from './ToolPads/Library';
import { SceneObjectList } from './ToolPads/SceneObjectList';
import { SceneEffectList } from './ToolPads/SceneEffectList';
import { ClipFolders } from './ToolPads/ClipFolders';
import { MoviePropertiesToolpad } from './ToolPads/MovieProperties';

const LEFT_TOOLPAD_ORDER: Record<string, number> = {
  movieProperties: 0,
  sceneList: 1,
  library: 2,
  sceneObjectList: 3,
  sceneEffectList: 4,
  clipFolders: 5,
};

const LEFT_RAIL_META: Partial<
  Record<ToolpadId, { icon: SidebarRailItem['icon']; titleKey: MessageKey }>
> = {
  movieProperties: { icon: <FileIcon width={16} height={16} />, titleKey: 'toolpads.movieProperties.title' },
  sceneList: { icon: <LayersIcon width={16} height={16} />, titleKey: 'toolpads.scenes.title' },
  library: { icon: <ImageIcon width={16} height={16} />, titleKey: 'toolpads.library.title' },
  sceneObjectList: { icon: <CubeIcon width={16} height={16} />, titleKey: 'toolpads.sceneObjects.title' },
  sceneEffectList: { icon: <MagicWandIcon width={16} height={16} />, titleKey: 'toolpads.sceneEffects.title' },
  clipFolders: { icon: <ArchiveIcon width={16} height={16} />, titleKey: 'toolpads.clipFolders.title' },
};

export function LeftSidebar() {
  const {
    toolpads,
    leftSidebarOpen,
    setLeftSidebar,
    openToolpad,
  } = useUIStore();

  const leftToolpads = Object.values(toolpads)
    .filter((tp) => tp.position === 'left')
    .sort((a, b) => {
      const aRank = LEFT_TOOLPAD_ORDER[a.id] ?? 100;
      const bRank = LEFT_TOOLPAD_ORDER[b.id] ?? 100;
      return aRank - bRank;
    });

  const railItems: SidebarRailItem[] = leftToolpads.flatMap((tp) => {
    const meta = LEFT_RAIL_META[tp.id];
    if (!meta) return [];
    return [{ id: tp.id, icon: meta.icon, titleKey: meta.titleKey }];
  });

  const handleExpandToolpad = (id: ToolpadId) => {
    openToolpad(id);
    setLeftSidebar(true);
  };

  return (
    <SidebarColumn
      side="left"
      isOpen={leftSidebarOpen}
      onCollapse={() => setLeftSidebar(false)}
      onExpandToolpad={handleExpandToolpad}
      items={railItems}
    >
      {leftToolpads.map((tp) => (
        <ToolpadWrapper key={tp.id} toolpadId={tp.id} />
      ))}
    </SidebarColumn>
  );
}

function ToolpadWrapper({ toolpadId }: { toolpadId: string }) {
  switch (toolpadId) {
    case 'library':
      return <Library />;
    case 'sceneList':
      return <SceneList />;
    case 'sceneObjectList':
      return <SceneObjectList />;
    case 'sceneEffectList':
      return <SceneEffectList />;
    case 'clipFolders':
      return <ClipFolders />;
    case 'movieProperties':
      return <MoviePropertiesToolpad />;
    default:
      return null;
  }
}
